import React, { useRef, useState, useEffect } from 'react';
import Hls from 'hls.js';
import { startLiveCatchupMonitor, schedulePlayLiveSync, HLS_LIVE_CONFIG } from '../lib/livePlaybackCatchup';
import { buildHlsPlayerStreamUrl } from '../lib/streamingUrl';
import { canPlayNativeHls, shouldPreferNativeHls } from '../lib/nativeHls';
import { attachSessionPlayback, isSessionActive } from '../lib/sessionActivity';

const VideoPlayerHls = ({
  videoStreaming,
  deviceType,
  nomeDispositivo,
  videoStreamingName,
  streamingActive = true,
  onPictureInPictureChange,
  streamingSenzaSeqMission = [],
}) => {
  const videoRef = useRef(null);
  const pipActiveRef = useRef(false);
  const [videoSrc, setVideoSrc] = useState('');
  const [reloadTrigger, setReloadTrigger] = useState(false);

  useEffect(() => {
    const updateVideoSrc = () => {
      const newVideoSrc = buildHlsPlayerStreamUrl({
        baseUrl: videoStreaming,
        seriale: nomeDispositivo,
        missionNum: videoStreamingName,
        deviceType,
        streamingSenzaSeqMission,
      });

      if (!newVideoSrc && deviceType) {
        console.warn(`Tipo dispositivo non riconosciuto o URL base mancante: ${deviceType}`);
      }

      setVideoSrc(newVideoSrc);
    };

    if (videoStreaming && nomeDispositivo && deviceType) {
      updateVideoSrc();
    } else {
      setVideoSrc('');
    }

  }, [videoStreaming, deviceType, nomeDispositivo, videoStreamingName, streamingSenzaSeqMission]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!streamingActive && videoElement && document.pictureInPictureElement === videoElement) {
      document.exitPictureInPicture().catch(() => {});
    }
  }, [streamingActive]);

  useEffect(() => {
    let hls = null;
    let reloadTimeoutId = null;
    let loadedMetadataHandler = null;
    let leavePipTimeoutId = null;
    let stopLiveCatchup = null;

    const syncToLiveEdge = () => {
      // Deferrato + condizionale: niente seek a ogni play (abortiva la ripresa).
      schedulePlayLiveSync(videoRef.current, () => hls);
    };

    const playVideo = (restorePictureInPicture = false) => {
      if (!isSessionActive() || !videoRef.current) {
        return;
      }

      const playPromise = videoRef.current.play();
      if (playPromise === undefined) {
        return;
      }

      playPromise.then(() => {
        if (
          restorePictureInPicture &&
          pipActiveRef.current &&
          document.pictureInPictureElement !== videoRef.current
        ) {
          videoRef.current.requestPictureInPicture().catch(() => {});
        }
      }).catch((error) => {
        const video = videoRef.current;
        if (!video || video.muted || !error || error.name !== 'NotAllowedError') {
          return;
        }
        video.muted = true;
        video.play().catch(() => {});
      });
    };

    const handleLeavePictureInPicture = () => {
      pipActiveRef.current = false;
      if (onPictureInPictureChange) {
        onPictureInPictureChange(false);
      }
      leavePipTimeoutId = setTimeout(() => playVideo(false), 200);
    };

    const handleEnterPictureInPicture = () => {
      pipActiveRef.current = true;
      if (onPictureInPictureChange) {
        onPictureInPictureChange(true);
      }
    };

    // Al play: sync al live solo se sei davvero indietro (altrimenti lascia ripartire).
    const handlePlay = () => {
      syncToLiveEdge();
    };

    // Tab di nuovo visibile: riparti e recupera il live solo se serve.
    const handleVisibilityChange = () => {
      if (!isSessionActive() || document.hidden || !videoRef.current) {
        return;
      }
      if (videoRef.current.paused) {
        playVideo(false);
      }
      schedulePlayLiveSync(videoRef.current, () => hls);
    };

    const initializeHls = () => {
      if (!videoSrc) {
        return;
      }

      const video = videoRef.current;
      const useNativeHls = shouldPreferNativeHls(video) || (!Hls.isSupported() && canPlayNativeHls(video));

      if (useNativeHls) {
        video.src = videoSrc;
        video.playsInline = true;
        loadedMetadataHandler = () => {
          playVideo(true);
        };
        video.addEventListener('loadedmetadata', loadedMetadataHandler);
      } else if (Hls.isSupported()) {
        hls = new Hls({
          ...HLS_LIVE_CONFIG,
        });
        hls.loadSource(videoSrc);
        hls.attachMedia(videoRef.current);

        hls.on(Hls.Events.ERROR, function (event, data) {
          if (data.fatal) {
            console.error('Errore fatale durante il caricamento del video:', data.type, data.details);

            if (!isSessionActive()) {
              return;
            }

            if (data.type !== Hls.ErrorTypes.NETWORK_ERROR || data.details !== Hls.ErrorDetails.MANIFEST_PARSING_ERROR) {
              if (reloadTimeoutId) {
                clearTimeout(reloadTimeoutId);
              }
              reloadTimeoutId = setTimeout(() => {
                if (!isSessionActive()) {
                  return;
                }
                setReloadTrigger((prevState) => !prevState);
              }, 5000);
            }
          }
        });

        hls.on(Hls.Events.MANIFEST_PARSED, function () {
          if (videoRef.current) {
            loadedMetadataHandler = () => {
              playVideo(true);
            };
            videoRef.current.addEventListener('loadedmetadata', loadedMetadataHandler);
          }
        });
      } else {
        console.error('Questo browser non supporta HLS');
      }
    };

    initializeHls();

    const detachSessionPlayback = attachSessionPlayback(videoRef.current, () => hls);
    const videoElement = videoRef.current;
    if (videoElement) {
      videoElement.addEventListener('leavepictureinpicture', handleLeavePictureInPicture);
      videoElement.addEventListener('enterpictureinpicture', handleEnterPictureInPicture);
      videoElement.addEventListener('play', handlePlay);
      stopLiveCatchup = startLiveCatchupMonitor(videoElement, () => hls);
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      detachSessionPlayback();

      if (stopLiveCatchup) {
        stopLiveCatchup();
        stopLiveCatchup = null;
      }

      if (leavePipTimeoutId) {
        clearTimeout(leavePipTimeoutId);
      }

      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (videoElement) {
        videoElement.removeEventListener('leavepictureinpicture', handleLeavePictureInPicture);
        videoElement.removeEventListener('enterpictureinpicture', handleEnterPictureInPicture);
        videoElement.removeEventListener('play', handlePlay);
        videoElement.pause();
        videoElement.removeAttribute('src');
        videoElement.load();
      }

      if (videoRef.current && loadedMetadataHandler) {
        videoRef.current.removeEventListener('loadedmetadata', loadedMetadataHandler);
        loadedMetadataHandler = null;
      }

      if (hls) {
        hls.destroy();
      }
      if (reloadTimeoutId) {
        clearTimeout(reloadTimeoutId);
      }
    };
  }, [videoSrc, reloadTrigger]);

  return (
    <video
      autoPlay
      playsInline
      controls
      ref={videoRef}
      className="custom-video-player-pc"
      key={reloadTrigger}
    ></video>
  );
};

export default VideoPlayerHls;
